import { useState, useRef, useEffect } from "react";
import { ChevronDown } from "lucide-react";
import { useWizardContext } from "../useWizardContext";
import { loginUser } from "../../../../api/auth";
import { useToast } from "../../../../context/ToastContext";
import { useGoogleLogin } from "@react-oauth/google";
import FacebookLogin from "@greatsumini/react-facebook-login";
import { FACEBOOK_APP_ID } from "src/config/facebookAuth";

const GoogleIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
  </svg>
);

const FacebookIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="#1877F2">
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
  </svg>
);

const RateTypeDropdown = ({ value, onChange, placeholder = "Per Day / Overall" }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const options = [
    { id: "per_day", label: "Per Day" },
    { id: "overall", label: "Overall" },
  ];

  const selectedOption = options.find((o) => o.id === value);

  return (
    <div className="relative shrink-0" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        className={`flex items-center justify-between gap-2 px-3 h-[36px] border rounded-lg text-xs sm:text-sm font-medium transition-all bg-white cursor-pointer min-w-[130px] ${
          isOpen
            ? "border-[#FF4C18] shadow-[0_4px_16px_rgba(255,76,24,0.10)] text-gray-900"
            : !value
              ? "border-gray-200 text-gray-400 hover:border-gray-300"
              : "border-gray-200 text-[#71717A] hover:border-gray-300"
        }`}
      >
        <span className="truncate">{selectedOption ? selectedOption.label : placeholder}</span>
        <ChevronDown
          className={`w-3.5 h-3.5 text-gray-400 shrink-0 transition-transform duration-200 ${
            isOpen ? "rotate-180 text-[#FF4C18]" : ""
          }`}
        />
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-100 rounded-xl shadow-[0_10px_30px_rgba(0,0,0,0.08)] py-1 z-30 animate-fadeIn min-w-full">
          {options.map((opt) => {
            const isSelected = value === opt.id;
            return (
              <button
                type="button"
                key={opt.id}
                onClick={() => {
                  onChange(opt.id);
                  setIsOpen(false);
                }}
                className={`w-full text-left px-2.5 py-1.5 text-xs sm:text-sm font-medium transition-colors flex items-center justify-between rounded-lg mx-auto max-w-[92%] cursor-pointer ${
                  isSelected
                    ? "bg-orange-50 text-[#FF4C18]"
                    : "text-gray-700 hover:bg-gray-50 hover:text-gray-900"
                }`}
              >
                <span>{opt.label}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default function Step3_BudgetAuth() {
  const {
    formData,
    updateField,
    updateLogin,
    nextStep,
    prevStep,
    isAuthenticated,
    setAuthenticated,
    setSuccess,
    closeWizard,
    isLoggingIn,
    setIsLoggingIn,
    submitEvent,
    isEditMode,
    eventId
  } = useWizardContext();
  const { showSuccess, showError } = useToast();

  const [showLogin, setShowLogin] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showPlanTooltip, setShowPlanTooltip] = useState(false);
  const tooltipRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (tooltipRef.current && !tooltipRef.current.contains(event.target)) {
        setShowPlanTooltip(false);
      }
    };
    if (showPlanTooltip) {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("touchstart", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, [showPlanTooltip]);

  const calculateDays = () => {
    if (!formData.startDate || !formData.endDate) return 1;
    const start = new Date(formData.startDate);
    const end = new Date(formData.endDate);
    if (isNaN(start.getTime()) || isNaN(end.getTime())) return 1;
    const s = new Date(start.getFullYear(), start.getMonth(), start.getDate());
    const e = new Date(end.getFullYear(), end.getMonth(), end.getDate());
    const diffTime = e.getTime() - s.getTime();
    const diffDays = Math.round(diffTime / (1000 * 3600 * 24)) + 1;
    return diffDays > 1 ? diffDays : 1;
  };

  const numberOfDays = calculateDays();
  const isMultiDates = Boolean(
    (formData.isMultiDay || (formData.startDate && formData.endDate && formData.startDate !== formData.endDate)) &&
    numberOfDays > 1
  );

  const [enteredAmount, setEnteredAmount] = useState(formData.budget?.amount || "");
  const [rateType, setRateType] = useState(() => {
    if (formData.budget?.rateType) return formData.budget.rateType;
    return isMultiDates ? "" : "per_day";
  });

  useEffect(() => {
    if (!isMultiDates && !rateType) {
      setRateType("per_day");
    }
  }, [isMultiDates, rateType]);

  const handleAmountChange = (val) => {
    setEnteredAmount(val);
    const num = Number(val || 0);
    const total = isMultiDates && rateType === "per_day" ? num * numberOfDays : num;
    updateField({
      budget: {
        ...formData.budget,
        amount: val ? total : "",
        rateType: rateType,
        currency: "INR",
      },
    });
  };

  const handleRateTypeChange = (type) => {
    setRateType(type);
    const num = Number(enteredAmount || 0);
    const total = isMultiDates && type === "per_day" ? num * numberOfDays : num;
    updateField({
      budget: {
        ...formData.budget,
        amount: enteredAmount ? total : "",
        rateType: type,
        currency: "INR",
      },
    });
  };

  const handleSubmitEvent = async () => {
    setIsLoading(true);
    try {
      const result = await submitEvent();
      if (result) {
        const targetEventId = (typeof result === "object" ? result.eventId : null) || eventId;
        showSuccess(isEditMode ? "Event updated successfully!" : "Event posted successfully!");
        if (isEditMode) {
          closeWizard();
          if (targetEventId) {
            window.location.href = `/events/${targetEventId}`;
          } else {
            window.location.reload();
          }
        } else {
          setSuccess(true);
          setTimeout(() => {
            closeWizard();
            if (targetEventId) {
              window.location.href = `/events/${targetEventId}`;
            } else {
              window.location.reload();
            }
          }, 1200);
        }
      }
    } catch (error) {
      showError(error.message || "Failed to create event");
    } finally {
      setIsLoading(false);
    }
  };

  const saveUserToLocal = (token, user) => {
    if (!token || !user) return;
    localStorage.setItem("authToken", token);
    localStorage.setItem("userId", user.id || user._id);
    if (user.customerId) localStorage.setItem("customerId", user.customerId);
    if (user.professionalId) localStorage.setItem("professionalId", user.professionalId);
    localStorage.setItem("role", user.role || "");
    if (user.professionalRole) localStorage.setItem("professionalRole", user.professionalRole);
    if (user.fullName) localStorage.setItem("fullName", user.fullName);
    if (user.email) localStorage.setItem("email", user.email);
    localStorage.setItem("isVerified", String(user.isVerified || false));
    
    const isProfessional = user.role === "professional";
    const onboardingComplete = isProfessional ? !!user.professionalId : true;
    localStorage.setItem("onboardingComplete", String(onboardingComplete));
    
    if (user.phone) localStorage.setItem("phone", user.phone);
    if (user.profilePictureUrl || user.profilePicture) {
      const picUrl = user.profilePictureUrl || user.profilePicture;
      localStorage.setItem("profilePicture", picUrl);
    }
  };

  const handleGoogleLogin = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      try {
        setIsLoading(true);
        const payload = {
          isGoogleAuth: true,
          googleToken: tokenResponse.access_token,
        };
        const res = await loginUser(payload);
        const userRole = (res?.user?.role || "").toLowerCase();
        if (userRole !== "customer") {
          showError("This account is registered as a professional. Please log in using a customer account to post requirements.");
          return;
        }
        saveUserToLocal(res.token, res.user);
        setAuthenticated();
        setIsLoggingIn(false);
        setShowLogin(false);
        showSuccess("Logged in successfully!");
      } catch (err) {
        showError(err.message || "Google authentication failed.");
      } finally {
        setIsLoading(false);
      }
    },
    onError: (error) => {
      showError("Google authentication failed.");
    },
  });

  const handleFacebookResponse = async (response) => {
    if (response.status === "unknown") return;

    try {
      setIsLoading(true);
      const payload = {
        isFacebookAuth: true,
        facebookToken: response.accessToken,
      };

      const res = await loginUser(payload);
      const userRole = (res?.user?.role || "").toLowerCase();
      if (userRole !== "customer") {
        showError("This account is registered as a professional. Please log in using a customer account to post requirements.");
        return;
      }
      saveUserToLocal(res.token, res.user);
      setAuthenticated();
      setIsLoggingIn(false);
      setShowLogin(false);
      showSuccess("Logged in successfully!");
    } catch (error) {
      showError(error.message || "Facebook authentication failed");
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogin = async (e) => {
    if (e) e.preventDefault();
    setIsLoading(true);
    try {
      const res = await loginUser({
        email: formData.loginDetails.email,
        password: formData.loginDetails.password
      });
      const userRole = (res?.user?.role || "").toLowerCase();
      if (userRole !== "customer") {
        showError("This account is registered as a professional. Please log in using a customer account to post requirements.");
        return;
      }
      saveUserToLocal(res.token, res.user);
      setAuthenticated(); // Shrinks steps to 3
      setIsLoggingIn(false);
      setShowLogin(false);
      showSuccess("Logged in successfully!");
    } catch (error) {
      showError(error.message || "Login failed");
    } finally {
      setIsLoading(false);
    }
  };

  // If showing Login form inside Step 3
  if (showLogin) {
    return (
      <div className="flex flex-col flex-1 h-full w-full max-w-lg mx-auto animate-fadeIn pt-1">
        {/* Social Login Buttons */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 mb-3">
          <FacebookLogin
            appId={FACEBOOK_APP_ID}
            onSuccess={handleFacebookResponse}
            onFail={(error) => {
              console.log("Facebook Login Failed!", error);
              showError("Facebook authentication failed");
            }}
            onProfileSuccess={(response) => {
              console.log("Facebook Profile Success!", response);
            }}
            render={({ onClick }) => (
              <button
                type="button"
                onClick={onClick}
                className="w-full flex items-center justify-center gap-2 h-[36px] px-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors bg-white font-medium text-gray-700 text-xs sm:text-sm cursor-pointer"
              >
                <FacebookIcon />
                <span>Continue with facebook</span>
              </button>
            )}
          />

          <button
            type="button"
            onClick={() => handleGoogleLogin()}
            className="w-full flex items-center justify-center gap-2 h-[36px] px-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors bg-white font-medium text-gray-700 text-xs sm:text-sm cursor-pointer"
          >
            <GoogleIcon />
            <span>Continue with Google</span>
          </button>
        </div>

        {/* Divider */}
        <div className="relative my-3 flex items-center justify-center">
          <div className="flex-grow border-t border-gray-200"></div>
          <span className="flex-shrink-0 mx-4 text-gray-400 text-xs sm:text-sm font-normal">Or continue with</span>
          <div className="flex-grow border-t border-gray-200"></div>
        </div>

        {/* Form Inputs */}
        <form onSubmit={handleLogin} className="flex flex-col flex-1 gap-2.5 w-full">
          <div className="flex flex-col gap-0.5">
            <label className="text-xs sm:text-sm font-semibold text-gray-900">Email address</label>
            <input
              type="email"
              placeholder="Enter your email address"
              value={formData.loginDetails.email}
              onChange={(e) => updateLogin({ email: e.target.value })}
              className="w-full h-[36px] px-3 border border-gray-200 rounded-lg focus:outline-none focus:border-[#FF4C18] text-xs sm:text-sm text-gray-900 placeholder:text-gray-400 bg-white"
              required
            />
          </div>

          <div className="flex flex-col gap-0.5">
            <label className="text-xs sm:text-sm font-semibold text-gray-900">Password</label>
            <input
              type="password"
              placeholder="Enter your password"
              value={formData.loginDetails.password}
              onChange={(e) => updateLogin({ password: e.target.value })}
              className="w-full h-[36px] px-3 border border-gray-200 rounded-lg focus:outline-none focus:border-[#FF4C18] text-xs sm:text-sm text-gray-900 placeholder:text-gray-400 bg-white"
              required
            />
            <div className="text-right mt-0.5">
              <button type="button" className="text-xs sm:text-sm text-[#FF4C18] font-medium hover:underline cursor-pointer">
                Forgot password?
              </button>
            </div>
          </div>

          {/* Footer Actions */}
          <div className="flex flex-row justify-between items-center gap-2 mt-auto pt-2 sm:pt-3 w-full">
            <button
              type="button"
              onClick={() => {
                setIsLoggingIn(false);
                setShowLogin(false);
              }}
              className="text-gray-400 hover:text-gray-700 font-normal text-xs sm:text-sm flex items-center gap-1.5 transition-colors cursor-pointer"
            >
              &larr; Back
            </button>

            <button
              type="submit"
              disabled={isLoading}
              className="bg-[#FF4C18] hover:bg-[#E63E0F] text-white px-6 py-2 rounded-lg font-medium text-xs sm:text-sm shadow-sm hover:shadow transition-all whitespace-nowrap disabled:opacity-50 cursor-pointer"
            >
              {isLoading ? "Submitting..." : "Submit"}
            </button>
          </div>
        </form>
      </div>
    );
  }

  const handleProceed = (action) => {
    if (!enteredAmount || Number(enteredAmount) <= 0) {
      showError("Please enter a valid budget amount.");
      return;
    }
    if (isMultiDates && !rateType) {
      showError("Please select whether the budget is Per Day or Overall.");
      return;
    }
    if (formData.additionalInfo) {
      const wordCount = formData.additionalInfo.trim().split(/\s+/).length;
      if (wordCount < 10) {
        showError("Please provide at least 10 words in 'Anything else we should know?' or leave it empty.");
        return;
      }
    }

    const num = Number(enteredAmount || 0);
    const total = isMultiDates && rateType === "per_day" ? num * numberOfDays : num;
    updateField({
      budget: {
        ...formData.budget,
        amount: total,
        rateType: rateType,
        currency: "INR",
      },
    });

    action();
  };

  return (
    <div className="flex flex-col gap-2.5 sm:gap-3 w-full animate-fadeIn flex-1 h-full pt-1">
      {/* What's your budget? */}
      <div>
        <h3 className="text-base sm:text-lg font-bold text-gray-950 mb-1.5">What's your budget?</h3>
        <div className="flex items-center gap-2.5 sm:gap-3 w-full">
          {/* Left Custom Dropdown Pill Box */}
          <RateTypeDropdown
            value={rateType}
            onChange={(val) => handleRateTypeChange(val)}
          />

          {/* Right Amount Input Box */}
          <div className="relative flex-1">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 font-normal text-xs sm:text-sm pointer-events-none">
              ₹
            </span>
            <input
              type="number"
              placeholder=""
              value={enteredAmount}
              onChange={(e) => handleAmountChange(e.target.value)}
              className="w-full pl-7 pr-3 h-[36px] border border-gray-200 rounded-lg focus:outline-none focus:border-[#FF4C18] text-xs sm:text-sm text-gray-900 bg-white [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
            />
          </div>
        </div>
      </div>

      {/* Tell professionals more about your requirement */}
      <div className="flex-1 flex flex-col min-h-[140px] sm:min-h-[160px]">
        <h3 className="text-base sm:text-lg font-bold text-gray-950 mb-1.5">Tell professionals more about your requirement</h3>
        <textarea
          placeholder="Add the venue, scope of work, specific requirements, preferences, or any other details professionals should know."
          value={formData.additionalInfo}
          onChange={(e) => updateField({ additionalInfo: e.target.value })}
          className="w-full flex-1 px-3 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:border-[#FF4C18] resize-none text-xs sm:text-sm text-gray-900 placeholder:text-gray-400 min-h-[120px] sm:min-h-[140px] bg-white leading-relaxed"
        />
      </div>

      {/* Navigation Buttons */}
      <div className="flex flex-row justify-between items-center gap-2 mt-auto pt-2 sm:pt-3 w-full">
        <button
          type="button"
          onClick={prevStep}
          className="text-gray-400 hover:text-gray-700 font-normal text-xs sm:text-sm flex items-center gap-1.5 transition-colors cursor-pointer"
        >
          &larr; Back
        </button>

        <div className="flex flex-row gap-3 items-center">
          {!isAuthenticated ? (
            <>
              <button
                type="button"
                onClick={() => {
                  setIsLoggingIn(true);
                  setShowLogin(true);
                }}
                className="text-[#FF4C18] hover:underline font-medium text-xs sm:text-sm px-2 py-1.5 cursor-pointer"
              >
                Log In
              </button>
              <button
                type="button"
                onClick={() => handleProceed(nextStep)}
                className="bg-[#FF4C18] hover:bg-[#E63E0F] text-white px-6 py-2 rounded-lg font-medium text-xs sm:text-sm shadow-sm hover:shadow transition-all whitespace-nowrap cursor-pointer"
              >
                Sign Up
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={() => handleProceed(handleSubmitEvent)}
              disabled={isLoading}
              className="bg-[#FF4C18] hover:bg-[#E63E0F] text-white px-6 py-2 rounded-lg font-medium text-xs sm:text-sm shadow-sm hover:shadow transition-all whitespace-nowrap disabled:opacity-50 cursor-pointer"
            >
              {isLoading ? "Submitting..." : "Submit Event"}
            </button>
          )}
        </div>
      </div>

    </div>
  );
}
